import { Component, ChangeDetectionStrategy, computed, input, output, signal } from '@angular/core';
import { TooltipModule } from 'ngx-bootstrap/tooltip';

/** Which edge of the entity the strip hugs, and therefore which side it adds on. */
export type AddButtonType = 'top' | 'right' | 'bottom' | 'left';

export interface AddEvent {
    type: AddButtonType;
    indexOffset: number;
}

/**
 * Where the new entity lands relative to this one — before it, or after it.
 * Exported for `web-resizable-add-buttons`, which is this strip with a drag
 * handle on it and answers the same question the same way.
 */
export const ADD_INDEX_OFFSET: Record<AddButtonType, number> = {
    top: 0,
    left: 0,
    bottom: 1,
    right: 1,
};

@Component({
    selector: 'web-add-buttons',
    templateUrl: './add-buttons.component.html',
    styleUrls: ['./add-buttons.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TooltipModule],
    host: {
        '[style]': 'hostStyle()',
        '(mouseenter)': 'isHovered.set(true)',
        '(mouseleave)': 'isHovered.set(false)',
    },
})
export class AddButtonsComponent {
    /**
     * Whether the strip is shown without being hovered — the host has hover of its
     * own, see {@link isShown}. Undefined because the callers hand it a threshold
     * off a hover state that has none while nothing is hovered.
     */
    public readonly show = input<boolean | undefined>(false);
    public readonly type = input.required<AddButtonType>();
    public readonly tooltipText = input<string | null>(null);
    public readonly isFullWidthBlock = input<boolean | undefined>(false);

    public readonly addEvent = output<AddEvent>();

    /**
     * Hover on the host, not on the strip inside it: the host is the wider
     * hit area that reaches past the entity's edge, which is what makes the
     * button appear before the pointer is on it.
     */
    protected readonly isHovered = signal<boolean>(false);

    public readonly isShown = computed<boolean>(() => Boolean(this.show()) || this.isHovered());

    /** The class the stylesheet keys the strip's placement off. */
    public readonly sideClass = computed<Record<AddButtonType, boolean>>(() => ({
        left: this.type() === 'left',
        right: this.type() === 'right',
        top: this.type() === 'top',
        bottom: this.type() === 'bottom',
    }));

    /**
     * The host is placed just outside the edge it adds on, and one of its four
     * offsets is keyed by that edge — hence a style map rather than four class
     * variants in the stylesheet.
     */
    public readonly hostStyle = computed<Record<string, string | number>>(() => {
        const type = this.type();
        const opacity = this.isShown() ? 1 : 0;

        if (type === 'left' || type === 'right') {
            return {
                [type]: this.isFullWidthBlock() ? '0' : '-19px',
                width: '20px',
                top: 0,
                bottom: 0,
                opacity,
            };
        }

        return {
            [type]: '-16px',
            height: '30px',
            left: 0,
            right: 0,
            opacity,
        };
    });

    public onClick(): void {
        const type = this.type();

        this.addEvent.emit({ type, indexOffset: ADD_INDEX_OFFSET[type] });
    }
}
